import { Box, Button, Chip, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import React from 'react';

import CustomAutocomplete from 'src/components/custom-autocomplete/custom-autocomplete';
import Iconify from 'src/components/iconify';
import { useResponsive } from 'src/hooks/use-responsive';

import { useFilter } from './filter.hook';

export interface FilterProps {
  getData: (filters?: any) => void;
  handleAddNew: () => void;
}

export default function Filter({ getData, handleAddNew }: FilterProps) {
  const {
    categoriesOptions,
    statusOptions,
    selectedCategory,
    selectedStatus,
    handleRemoveCategoryFilter,
    handleRemoveStatusFilter,
    handleRemoveAllFilters,
    handleSelectCategory,
    handleSelectStatus,
    onSearch,
  } = useFilter(getData);

  const responsive = useResponsive;
  const isXs = responsive('down', 'sm');
  const isMd = responsive('down', 'lg');

  return (
    <div data-testid="filter-render-test">
      <Stack alignItems="center" direction={isMd ? 'column' : 'row'} spacing={2} width="100%">
        <Stack direction="row" justifyContent="space-between" alignItems="center" width={isMd ? '100%' : 'auto'}>
          <Typography typography="bold-hd-xs" noWrap>
            Categorias
          </Typography>
          {isMd && (
            <Button variant="text" color="primary" onClick={handleAddNew}>
              <Typography typography="bold-bd-md">Adicionar Novo</Typography>
            </Button>
          )}
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          flexWrap={isMd ? 'wrap' : 'nowrap'}
          flexGrow={1}
          justifyContent="flex-start"
          alignItems="center"
        >
          <TextField
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Busque a categoria desejada"
            InputProps={{
              sx: { borderRadius: 10 },
              endAdornment: (
                <InputAdornment position="end" sx={{ mr: 1 }}>
                  <Iconify icon="ri:search-line" sx={{ color: 'text.disabled', width: 20, height: 20 }} />
                </InputAdornment>
              ),
            }}
            inputProps={{
              style: { padding: '8px 16px' },
            }}
            fullWidth={!!isXs}
            sx={{
              width: 'auto',
              '& .MuiInputBase-root': {
                width: '493px',
              },
            }}
          />
          <CustomAutocomplete
            handleSelectOption={handleSelectCategory}
            options={categoriesOptions}
            placeholder="Tipo"
            sx={{ '.MuiOutlinedInput-root': { p: 0 }, minWidth: 140 }}
            disableTags
            disableCloseOnSelect
          />
          <CustomAutocomplete
            handleSelectOption={handleSelectStatus}
            options={statusOptions}
            placeholder="Status"
            disableTags
            disableCloseOnSelect
            sx={{ '.MuiOutlinedInput-root': { p: 0 }, minWidth: 120 }}
          />
        </Stack>
        {!isMd && (
          <Button variant="text" color="primary" onClick={handleAddNew} sx={{ ml: 'auto' }}>
            <Typography typography="bold-bd-md">Adicionar Novo</Typography>
          </Button>
        )}
      </Stack>
      <Box height={10} />
      {(selectedCategory || selectedStatus) && (
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {selectedCategory && (
            <Chip
              label={selectedCategory.label}
              color="primary"
              variant="outlined"
              sx={{ borderRadius: 100 }}
              onDelete={() => handleRemoveCategoryFilter(selectedCategory.value)}
              deleteIcon={<Iconify icon="ri:close-line" width={16} />}
            />
          )}
          {selectedStatus && (
            <Chip
              label={selectedStatus.label}
              color="primary"
              variant="outlined"
              sx={{ borderRadius: 100 }}
              onDelete={() => handleRemoveStatusFilter(selectedStatus.value)}
              deleteIcon={<Iconify icon="ri:close-line" width={16} />}
            />
          )}
          <Button
            onClick={handleRemoveAllFilters}
            variant="text"
            sx={{
              typography: 'body-sm',
              textDecoration: 'underline',
              ':hover': {
                bgcolor: 'transparent',
                color: 'brand.200',
                textDecoration: 'underline',
              },
            }}
            disableRipple
            color="primary"
          >
            Limpar Filtros
          </Button>
        </Stack>
      )}
    </div>
  );
}
